import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ratingsService } from '../../services/ratingsService';
import { SectionSkeleton, SectionError } from './CompanyProfile';
import { fmtUsDate } from './format';
import { firmDisplayName } from '../../utils/ratingFirm';

interface Props { symbol: string; }

const PER_PAGE = 10;

type Tone = 'bull' | 'bear' | 'neutral';

// Firms use dozens of wordings for the same three stances, so match on
// substrings rather than trying to enumerate every exact grade string.
const BEAR = ['sell', 'underweight', 'underperform', 'reduce', 'negative'];
const BULL = ['buy', 'overweight', 'outperform', 'accumulate', 'positive', 'add'];

function gradeTone(g: string | null): Tone {
  if (!g) return 'neutral';
  const s = g.toLowerCase();
  // Bear first: "moderate sell" and "underperform" would otherwise be missed,
  // and nothing bullish contains a bearish term.
  if (BEAR.some((w) => s.includes(w))) return 'bear';
  if (BULL.some((w) => s.includes(w))) return 'bull';
  return 'neutral'; // hold, neutral, market/sector perform, equal weight...
}

const TONE_STYLE: Record<Tone, string> = {
  bull: 'bg-bull-blue/16 text-bull-blue',
  bear: 'bg-bear-red/16 text-bear-red',
  neutral: 'bg-gray-700/40 text-gray-300',
};

function GradeBadge({ grade }: { grade: string | null }): React.ReactElement {
  if (!grade) return <span className="text-gray-600">&mdash;</span>;
  return (
    <span
      className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${
        TONE_STYLE[gradeTone(grade)]
      }`}
    >
      {grade}
    </span>
  );
}

function Pagination({ page, totalPages, onPrev, onNext }: {
  page: number; totalPages: number; onPrev: () => void; onNext: () => void;
}): React.ReactElement {
  return (
    <div className="flex items-center gap-2 mt-3 justify-end">
      <button
        disabled={page === 1}
        onClick={onPrev}
        className="px-2 py-1 text-xs rounded border border-dark-border text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      >
        &larr; Prev
      </button>
      <span className="text-xs text-gray-400">{page} / {totalPages}</span>
      <button
        disabled={page >= totalPages}
        onClick={onNext}
        className="px-2 py-1 text-xs rounded border border-dark-border text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      >
        Next &rarr;
      </button>
    </div>
  );
}

export function AnalystRatingsSection({ symbol }: Props): React.ReactElement {
  const [page, setPage] = useState(1);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['tickerRatingsInline', symbol, page],
    queryFn: () => ratingsService.fetchRatings({ page, perPage: PER_PAGE, search: symbol }),
    staleTime: 5 * 60 * 1000,
    enabled: !!symbol,
  });

  if (isLoading) return <SectionSkeleton />;
  if (isError) return <SectionError message="Failed to load analyst ratings" />;

  const ratings = data?.results ?? [];
  const totalPages = data?.total_pages ?? 1;

  return (
    <section className="p-4 border-b border-dark-border">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">Analyst Ratings</h2>
        {(data?.count ?? 0) > 0 && (
          <span className="text-xs text-gray-400">{data?.count} ratings</span>
        )}
      </div>
      {!ratings.length ? (
        <p className="text-gray-400 text-sm">No analyst ratings for {symbol}</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="border-b border-dark-border">
                  {['Date', 'Firm', 'Previous', 'New Rating', 'Price'].map((h) => (
                    <th key={h} className="py-1.5 pr-5 text-left text-[10px] uppercase tracking-wider text-gray-400 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ratings.map((r) => (
                  <tr key={r.id} className="border-b border-dark-border/40 hover:bg-dark-bg/60 transition-colors">
                    <td className="py-1.5 pr-5 font-numeric text-gray-400">{fmtUsDate(r.published_date)}</td>
                    <td className="py-1.5 pr-5 text-gray-200">{firmDisplayName(r.grading_company)}</td>
                    <td className="py-1.5 pr-5"><GradeBadge grade={r.previous_grade} /></td>
                    <td className="py-1.5 pr-5"><GradeBadge grade={r.new_grade} /></td>
                    <td className="py-1.5 pr-5 font-numeric text-white">
                      {r.price_when_posted != null ? `$${r.price_when_posted.toFixed(2)}` : '\u2014'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              onPrev={() => setPage((p) => p - 1)}
              onNext={() => setPage((p) => p + 1)}
            />
          )}
        </>
      )}
    </section>
  );
}
